import { HomeCategory, isMintGameCode, resolveGameCategory } from "./categories";
import { gradedCountValue, RankableGame } from "./featured";
import { matchesSearch } from "./text";

/*
  The home pipeline's first two stages (§4.2), shared by both sections.

      featured:  category rule -> home search -> rank -> slice(10) -> maybe re-order
      A-Z:       category rule -> home search -> dedupe -> group

  Only the shared half lives here; the two tails are selectFeaturedGames (featured.ts)
  and groupGamesAlphabetically (grouping.ts). Composing it once is what makes the two
  sections provably agree about which games exist, which is half of §12.3 and §12.6.
*/

export interface HomeGame extends RankableGame {
  gameCode: string;
  category?: string | null;
  /* Absent on a backend without scope=population, which reads as not archived. */
  archived?: boolean;
}

/*
  New Zealand Mint is excluded by gameCode AND by the category rule (§5.2, D16), not by
  the category rule alone. The rule already drops it while category === "Coins" holds,
  but D12 says HQ is actively re-categorising games, and a blanked category would put a
  Coins game back on the public home page through the uncategorised-to-TCG fallback.
*/
export const isHomeGameVisible = <T extends HomeGame>(
  game: T,
  section: HomeCategory
): boolean =>
  !isMintGameCode(game.gameCode) && resolveGameCategory(game.category) === section;

/*
  Order is not load-bearing here — both stages are filters — but it is kept in §4.2's
  order so the code reads as the spec does.
*/
export const selectHomeGames = <T extends HomeGame>(
  games: readonly T[],
  section: HomeCategory,
  query: string
): T[] =>
  games.filter(
    (game) => isHomeGameVisible(game, section) && matchesSearch(game.name, query)
  );

export interface ArchivePartition<T> {
  live: T[];
  archived: T[];
}

/*
  The third stage: live games feed Featured and "All …", archived ones the Archive
  section. An archived game is only public because of its gradings, so one with none is
  dropped rather than listed at zero.
*/
export const partitionArchived = <T extends HomeGame>(
  games: readonly T[]
): ArchivePartition<T> => ({
  live: games.filter((game) => game.archived !== true),
  archived: games.filter(
    (game) => game.archived === true && gradedCountValue(game.cardsGraded) > 0
  ),
});
